import * as THREE from 'three';
import DxfArcParser, { helpers } from './dxfEntities/dxf_arc';
import DxfLineParser from './dxfEntities/dxf_line';
import DxfPointParser from './dxfEntities/dxf-point.ts';
import DxfSurfaceParser from './dxfEntities/dxf_surface';
import DxfTextParser from './dxfEntities/dxf_text';
import { dxfHelpers } from './dxfHelpers';
import DxfPropertiesManager from './dxfPropertiesManager';
import * as mathHelpers from '../../utilities/math-helpers';
import { getArcVerticesFromEntity } from './dxfEntities/get-arc-vertices';

const lineparser = new DxfLineParser();
const textparser = new DxfTextParser();
const arcParser = new DxfArcParser();
const surfaceparser = new DxfSurfaceParser();
const pointparser = new DxfPointParser();

const propertiesManager = new DxfPropertiesManager();

export const parseUtilities = {
  /**
   *  Extract the DXF-extents from the DXF-data
   */
  extractDxfExtents(dxfParserData) {
    let extMin = dxfParserData.header.$EXTMIN;
    let extMax = dxfParserData.header.$EXTMAX;

    return {
      min: new THREE.Vector3(extMin.x, extMin.y, extMin.z),
      max: new THREE.Vector3(extMax.x, extMax.y, extMax.z),
    };
  },

  /**
   *  Extract the dxf units from the header
   */
  extractDxfUnits(dxfParserData) {
    return dxfParserData.header.$INSUNITS;
  },

  /**
   * This will take the raw data returned by the dxf-parser
   * and return it in a form which can be used to generate
   * the Dxf ElemObj used in the scene.
   */
  processDxfData(dxfParserData) {
    let data = {
      layers: dxfParserData.tables.layer.layers,
      lineTypes: dxfParserData.tables.lineType.lineTypes,
      dxfBlocks: dxfParserData.blocks,
      entities: dxfParserData.entities,
      layerData: {},
    };

    try {
      let layerData = this.processEntities(data);
      return {
        header: dxfParserData.header,
        data: layerData,
        contains3dPolyline: data.contains3dPolyline,
      };
    } catch (err) {
      if (err.message && err.error) {
        throw err;
      }

      throw { message: 'Det gick inte att processera datan', error: err };
    }
  },

  getThreeData(dxfData) {
    if (!dxfData) {
      return {};
    }

    let dxf = { extents: this.extractDxfExtents(dxfData), units: this.extractDxfUnits(dxfData) };

    let populatedData = this.populateLayers(dxfData.data);
    dxf.layers = populatedData.layers;
    dxf.containsSurface = populatedData.containsSurface;
    dxf.contains3dPolyline = dxfData.contains3dPolyline;
    return dxf;
  },

  /**
   * Loops through the entities, main types of entitites
   * Can either be real enteties or inserts which picks
   * data from corresponding block
   * Returns the organised layerData as an object
   * @param {*} data data which to process
   */
  processEntities(data) {
    for (let i = 0; i < data.entities.length; i++) {
      let parserEntity = data.entities[i];

      //Skips entity if part of a hidden or frozen layers
      if (data.layers[parserEntity.layer].frozen || !data.layers[parserEntity.layer].visible) {
        continue;
      }

      //Skips hidden enteties
      if (parserEntity.visible === 1) {
        continue;
      }

      if (parserEntity.type === 'INSERT') {
        let insert = parserEntity;
        let insertBlock = data.dxfBlocks[parserEntity.name];
        this.parseInsert(data, insert, insertBlock);
      } else {
        this.parseEntity(data, parserEntity, undefined);
      }
    }

    this.processLayerData(data.layerData);
    return data.layerData;
  },

  processLayerData(layerData) {
    for (let layer in layerData) {
      // eslint-disable-next-line no-prototype-builtins
      if (layerData.hasOwnProperty(layer) && layerData[layer].lineSegments.length > 0) {
        for (let i = 0; i < layerData[layer].lineSegments.length; i++) {
          let lineSegments = layerData[layer].lineSegments[i];
          lineSegments.dottedVertices = lineparser.generateDashedLines(lineSegments);
        }
      }
    }
  },

  /**
   * Generates an object full layers, with each layer as an object 3D and corresponding
   * Enteties for that layers as children to the layer.
   * @param {*} layerData Object which contains basic data of the layers and geometries
   */
  populateLayers(layerData) {
    let layers = {};
    let containsSurface = false;

    for (let layer in layerData) {
      // eslint-disable-next-line no-prototype-builtins
      if (layerData.hasOwnProperty(layer)) {
        let layerObj = new THREE.Object3D();
        layerObj.name = layer;
        layers[layer] = { geometry: layerObj, texts: layerData[layer].texts };

        if (layerData[layer].texts.length > 0) {
          layerObj.add(textparser.generateTextCollection(layerData[layer].texts));
        }

        for (let i = 0; i < layerData[layer].lineSegments.length; i++) {
          layerObj.add(lineparser.generateLines(layerData[layer].lineSegments[i]));
        }

        for (let i = 0; i < layerData[layer].points.length; i++) {
          const pointCollection = pointparser.generatePoints(layerData[layer].points[i]);
          if (pointCollection) {
            layerObj.add(pointCollection);
          }
        }

        if (layerData[layer].meshes.length > 0) {
          containsSurface = true;
        }
        for (let i = 0; i < layerData[layer].meshes.length; i++) {
          layerObj.add(surfaceparser.generate3DSurface(layerData[layer].meshes[i]));
        }
      }
    }
    return { layers: layers, containsSurface: containsSurface };
  },

  /**
   * Loops through the block to get data for INSERT entity
   * @param {*} insert The insert entity
   * @param {*} insertBlock block which insert points to
   */
  parseInsert(data, insert, insertBlock) {
    if (!insertBlock.entities) {
      return;
    }

    for (let i = 0; i < insertBlock.entities.length; i++) {
      const entity = insertBlock.entities[i];
      if (entity.extrusionDirection && entity.type !== 'ARC') {
        continue;
      }

      if (entity.type === 'INSERT') {
        const innerInsert = { ...entity };
        dxfHelpers.transformInsertScale(insert, innerInsert);

        innerInsert.rotation = ((innerInsert.rotation || 0) + (insert.rotation || 0)) % 360;

        const transformedPosition = dxfHelpers.rotateVector(
          innerInsert.position,
          mathHelpers.degreesToRadians(insert.rotation) || 0,
        );
        transformedPosition.z = innerInsert.position.z;

        innerInsert.position = helpers.addCoords(insert.position, transformedPosition);

        innerInsert.color = innerInsert.color === undefined ? insert.color : innerInsert.color;
        innerInsert.colorIndex =
          innerInsert.colorIndex === undefined ? insert.colorIndex : innerInsert.colorIndex;

        let innerInsertBlock = data.dxfBlocks[entity.name];
        this.parseInsert(data, innerInsert, innerInsertBlock);
      } else {
        this.parseEntity(data, entity, insert);
      }
    }
  },

  /**
   * Takes an entity object and checks what type it is to process it correctly,
   * generate the geometries, materials to be used for Three and put it in the
   * dxf object.
   * @param {*} entity Entity to be parsed
   * @param {*} insert Insert object which might affect the properties
   */
  parseEntity(data, entity, insert) {
    if (entity.extrusionDirection && entity.type !== 'ARC') {
      return;
    }

    if (entity.type === 'ATTDEF') {
      if (!insert) {
        console.error('ATTDEF without insert identified', entity);
        return;
      }

      entity = textparser.createTextEntityFromAttdef(entity, insert.entities);
    }

    let properties = {};

    properties.color = propertiesManager.parseColor(data, entity, insert);

    let lineTypeProperties = propertiesManager.parseLineType(data, entity, insert);
    properties.lineTypeScale = lineTypeProperties.scale;
    properties.lineType = lineTypeProperties.pattern;

    properties.lineWidth = propertiesManager.parseLineWidth(data, entity, insert);

    let newEntityEntry;

    if (entity.type === 'LINE' || entity.type === 'LWPOLYLINE' || entity.type === 'POLYLINE') {
      if (entity.isPolyfaceMesh) {
        newEntityEntry = surfaceparser.processPolyfaceMesh(entity, insert);
      } else {
        newEntityEntry = lineparser.processLineData(entity, insert);
      }
    } else if (entity.type === 'CIRCLE') {
      newEntityEntry = lineparser.processArcData(entity, arcParser.getCircleVertices, insert);
    } else if (entity.type === 'ARC') {
      newEntityEntry = lineparser.processArcData(entity, getArcVerticesFromEntity, insert);
    } else if (entity.type === 'SPLINE') {
      newEntityEntry = lineparser.processArcData(entity, arcParser.getSplineVertices, insert);
    } else if (entity.type === 'ELLIPSE') {
      newEntityEntry = lineparser.processArcData(entity, arcParser.getEllipseVertices, insert);
    } else if (
      (entity.type === 'TEXT' || entity.type === 'MTEXT') &&
      entity.text &&
      entity.text.length > 0
    ) {
      newEntityEntry = textparser.processText(entity, properties, insert);
    } else if (entity.type === '3DFACE') {
      newEntityEntry = surfaceparser.process3DFace(entity, insert);
    } else if (entity.type === 'POINT') {
      newEntityEntry = pointparser.processPoint(entity, insert);
    }

    if (!newEntityEntry) {
      return;
    }

    if (entity.is3dPolyline) {
      data.contains3dPolyline = true;
    }

    newEntityEntry.layer = entity.layer;
    newEntityEntry.properties = properties;
    this.mergeLayerData(data.layerData, newEntityEntry);
  },

  mergeLayerData(layerData, entity) {
    if (!layerData[entity.layer]) {
      layerData[entity.layer] = {
        visible: true,
        lineSegments: [],
        texts: [],
        meshes: [],
        points: [],
      };
    }

    if (entity.textRows) {
      layerData[entity.layer].texts.push(entity);
    } else if (entity.points) {
      let index = propertiesManager.findMatchingProperties(
        layerData[entity.layer].points,
        entity.properties,
      );
      if (index === false) {
        layerData[entity.layer].points.push(entity);
      } else {
        for (let i = 0; i < entity.points.length; i++) {
          layerData[entity.layer].points[index].points.push(entity.points[i]);
        }
      }
    } else if (entity.vertices) {
      let index = propertiesManager.findMatchingProperties(
        layerData[entity.layer].lineSegments,
        entity.properties,
      );
      if (index === false) {
        layerData[entity.layer].lineSegments.push(entity);
      } else {
        for (let i = 0; i < entity.vertices.length; i++) {
          layerData[entity.layer].lineSegments[index].vertices.push(entity.vertices[i]);
        }
      }
    } else if (entity.faces) {
      //find matching index...
      let index = propertiesManager.findMatchingProperties(
        layerData[entity.layer].meshes,
        entity.properties,
      );

      if (index === false) {
        layerData[entity.layer].meshes.push(entity);
      } else {
        for (let i = 0; i < entity.faces.length; i++) {
          layerData[entity.layer].meshes[index].faces.push(entity.faces[i]);
        }
      }
    }
  },
};
